import React, { useState, useEffect, useRef } from "react";
import {
	List,
	Input,
	Avatar,
	Button,
	Upload,
	Tag,
	Modal,
	Row,
	Col,
	notification,
	Form,
	Space,
	Mentions,
} from "antd";
import { Comment } from "@ant-design/compatible";
import { InboxOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { getFileNameFromPath, downloadFile } from "../../utils/helper";
import "./index.css";
import commentService from "../../services/comment";
// import FileViewer from 'react-file-viewer';
import userService from "../../services/user";

const { getMentions } = Mentions;

const CommentSystem = ({ id, form_id, status, comment_status }) => {
	const [comments, setComments] = useState([]);
	const [newComment, setNewComment] = useState("");
	const [attachmentList, setAttachmentList] = useState([]);
	const [fileList, setFileList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showUploader, setShowUploader] = useState(false);
	const [newCommentAdded, setNewCommentAdded] = useState(false);
	const [form] = Form.useForm();
	const [mentionSuggestions, setMentionSuggestions] = useState([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [docs, setDocs] = useState({
		uri: "",
		fileType: "",
		fileName: "",
	});

	const [mentionedUsers, setMentionedUsers] = useState([]);
	const mentionInputRef = useRef(null);

	const [expandedComments, setExpandedComments] = useState({});

	const [editingCommentId, setEditingCommentId] = useState(null);
	const [editText, setEditText] = useState("");

	const handleEdit = (comment) => {
		setEditingCommentId(comment.id);
		setEditText(comment.comment);
	};

	const handleUpdateComment = async (id) => {
		try {
			const res = await commentService.updateComment(id, { comment: editText });

			setComments((prev) =>
				prev.map((c) => (c.id === id ? { ...c, comment: editText } : c)),
			);

			setEditingCommentId(null);
			notification.success({ message: "Updated" });
		} catch (err) {
			notification.error({
				message:
					"Error : " +
					(err.response?.data?.message ||
						"There was an error updating the comment."),
			});
		}
	};

	const handleDelete = async (id) => {
		Modal.confirm({
			title: "Delete Comment",
			content: "Are you sure you want to delete this comment?",
			okText: "Yes",
			cancelText: "No",
			okType: "danger",

			onOk: async () => {
				try {
					await commentService.deleteComment(id);

					setComments((prev) => prev.filter((c) => c.id !== id));

					notification.success({
						message: "Deleted",
						description: "Comment deleted successfully",
					});
				} catch (err) {
					notification.error({
						message:
							"Delete failed " +
							(err.response?.data?.message ||
								"There was an error deleting the comment."),
					});
				}
			},
		});
	};

	const handleDeleteAttachment = async (commentId, attachmentId) => {
		Modal.confirm({
			title: "Delete Attachment",
			content: "Are you sure you want to delete this attachment?",
			okText: "Yes",
			cancelText: "No",
			okType: "danger",

			onOk: async () => {
				try {
					await commentService.deleteAttachment(attachmentId);

					setComments((prev) =>
						prev.map((c) =>
							c.id === commentId
								? {
										...c,
										comment_attachments: c.comment_attachments.filter(
											(a) => a.id !== attachmentId,
										),
									}
								: c,
						),
					);

					notification.success({
						message: "Deleted",
						description: "Attachment deleted successfully",
					});
				} catch (err) {
					notification.error({
						message:
							"Delete failed " +
							(err.response?.data?.message ||
								"There was an error deleting the attachment."),
					});
				}
			},
		});
	};

	const toggleCommentExpansion = (index) => {
		setExpandedComments((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const fetchFileInfo = (attachmentUrl) => {
		const fileType = getFileTypeFromUrl(attachmentUrl);
		setDocs({
			uri: attachmentUrl,
			fileType: fileType,
			fileName: getFileNameFromPath(attachmentUrl),
		});
		setIsModalVisible(true);
	};

	const getFileTypeFromUrl = (url) => {
		const extension = url.split(".").pop().toLowerCase();
		switch (extension) {
			case "jpg":
				return "jpg";
			case "jpeg":
				return "jpeg";
			case "png":
				return "png";
			case "gif":
				return "gif";
			case "doc":
				return "doc";
			case "docx":
				return "doc";
			case "xls":
				return "xls";
			case "xlsx":
				return "xls";
			case "ppt":
				return "ppt";
			case "pptx":
				return "ppt";
			case "pdf":
				return "pdf";
			case "txt":
				return "txt";
			default:
				return "";
		}
	};

	const handleCancel = () => {
		setIsModalVisible(false);
	};

	useEffect(() => {
		const getComments = async () => {
			setLoading(true);
			try {
				const response = await commentService.getComments(id, form_id);
				setComments(response);
				setLoading(false);
			} catch (error) {
				setLoading(false);
			}
		};

		getComments();
	}, [id]);

	const handleSearchUsers = async (query) => {
		if (query.length >= 2) {
			try {
				const response = await userService.searchUsers(query);
				const suggestions = response.data.map((user) => ({
					value: user.name,
					label: user.name,
					id: user.id,
				}));
				setMentionSuggestions(suggestions);
			} catch (error) {
				console.error("Error searching users:", error);
			}
		} else {
			setMentionSuggestions([]);
		}
	};

	const handleMentionSelect = (option) => {
		const user = mentionSuggestions.find((user) => user.value === option.value);
		if (
			user &&
			!mentionedUsers.some((mentionedUser) => mentionedUser.id === user.id)
		) {
			setMentionedUsers([...mentionedUsers, user]);
		}
	};

	const handleMentionChange = (contentState) => {
		setNewComment(contentState);
	};

	const handleTagClose = (removedUserId) => {
		setMentionedUsers(
			mentionedUsers.filter((user) => user.id !== removedUserId),
		);
	};

	const handleCommentChange = (e) => {
		setNewComment(e.target.value);
	};

	const props = {
		onRemove: (file) => {
			const index = fileList.indexOf(file);
			const newFileList = fileList.slice();
			newFileList.splice(index, 1);
			setFileList(newFileList);
		},
		beforeUpload: (file) => {
			setFileList((prevFileList) => {
				return prevFileList ? [...prevFileList, file] : [file];
			});
			return false;
		},
		fileList,
	};

	const handleAddComment = async () => {
		form
			.validateFields()
			.then(async (values) => {
				if (newComment || fileList.length > 0) {
					const timestamp = moment().format("MMMM Do YYYY, h:mm a");
					const formData = new FormData();
					formData.append("form_id", form_id);
					formData.append("key", id);
					formData.append("comment", newComment ? newComment : "-");
					formData.append("timestamp", timestamp);
					formData.append(
						"mentioned_user_ids",
						JSON.stringify(mentionedUsers.map((user) => user.id)),
					);
					if (fileList) {
						fileList.forEach((file, index) => {
							formData.append(`attachments[${index}]`, file);
						});
					}
					if (!newComment.trim() && fileList.length === 0) {
						notification.error({
							message: "Comment or Attachment Required",
							description: "Please enter a comment or upload an attachment.",
						});
						return;
					}
					setLoading(true);

					try {
						const response = await commentService.postComments(formData);
						setLoading(false);
						setFileList([]);
						setNewComment("");
						setAttachmentList([]);
						setNewCommentAdded(true);
						form.resetFields();
						setMentionedUsers([]);
						setTimeout(() => {
							setComments([...comments, response.data]);
						}, 500);

						notification.success({
							message: "Comment Added",
							description: "Comment added successfully",
						});
					} catch (error) {
						console.error("Comment post error:", error);
						notification.error({
							message: "Error",
							description:
								error.response?.data?.message ||
								"There was an error posting the comment.",
						});
						setLoading(false);
					} finally {
						setFileList([]);
					}
				}
			})
			.catch((error) => {
				console.error("Validation failed:", error);
			});
	};

	const toggleUploader = () => {
		setShowUploader(!showUploader);
		setNewCommentAdded(true);
	};

	return (
		<Row gutter={16}>
			<Col span={24}>
				{comments && comments.length > 0 && (
					<List
						header={`${comments.length} Comments`}
						itemLayout='horizontal'
						dataSource={comments}
						renderItem={(comment, index) => (
							<li
								key={index}
								className={newCommentAdded ? "elementToFadeInAndOut" : null}
							>
								<Comment
									author={comment.user.name}
									avatar={<Avatar src='user-avatar-url' />}
									// content={
									// 	<div style={{ whiteSpace: "pre-wrap" }}>
									// 		{comment.comment && comment.comment.length > 250 ? (
									// 			<>
									// 				{expandedComments[index]
									// 					? comment.comment
									// 					: `${comment.comment.substring(0, 250)}... `}
									// 				<Button
									// 					type="link"
									// 					size="small"
									// 					onClick={() => toggleCommentExpansion(index)}
									// 					style={{ padding: 0 }}
									// 				>
									// 					{expandedComments[index] ? "View Less" : "View More"}
									// 				</Button>
									// 			</>
									// 		) : (
									// 			comment.comment
									// 		)}
									// 	</div>
									// }
									content={
										editingCommentId === comment.id ? (
											<>
												<Input.TextArea
													value={editText}
													onChange={(e) => setEditText(e.target.value)}
													autoSize={{ minRows: 2, maxRows: 6 }}
												/>
												<Button
													type='primary'
													size='small'
													onClick={() => handleUpdateComment(comment.id)}
													style={{ marginTop: 5 }}
												>
													Save
												</Button>
												<Button
													size='small'
													onClick={() => setEditingCommentId(null)}
													style={{ marginLeft: 5 }}
												>
													Cancel
												</Button>
											</>
										) : (
											<div style={{ whiteSpace: "pre-wrap" }}>
												{comment.comment}
											</div>
										)
									}
									datetime={moment(comment.created_at).format(
										"MMMM Do YYYY, h:mm a",
									)}
									actions={[
										...(comment.permissions?.can_edit
											? [<span onClick={() => handleEdit(comment)}>Edit</span>]
											: []),
										...(comment.permissions?.can_edit
											? [
													<span onClick={() => handleDelete(comment.id)}>
														Delete
													</span>,
												]
											: []),
									]}
								/>
								<div>
									{comment.comment_attachments &&
									comment.comment_attachments.length > 0 ? (
										<table
											style={{
												width: "100%",
												borderCollapse: "collapse",
												marginBottom: 12,
											}}
										>
											<thead>
												<tr>
													<th
														style={{
															textAlign: "left",
															padding: 8,
															borderBottom: "1px solid #eee",
														}}
													>
														Title
													</th>
													<th
														style={{
															textAlign: "left",
															padding: 8,
															borderBottom: "1px solid #eee",
														}}
													>
														Preview
													</th>
													<th
														style={{
															textAlign: "left",
															padding: 8,
															borderBottom: "1px solid #eee",
														}}
													>
														Action
													</th>
												</tr>
											</thead>
											<tbody>
												{comment.comment_attachments.map((attachment, i) => {
													const isImage = (filename) =>
														/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename);
													const fileUrl = attachment.filename;
													const fileTitle =
														attachment.original_title ||
														getFileNameFromPath(attachment.fileName);
													return (
														<tr key={`${comment.id}-${i}`}>
															<td
																style={{ padding: 8, verticalAlign: "middle" }}
															>
																{fileTitle}
															</td>
															<td
																style={{ padding: 8, verticalAlign: "middle" }}
															>
																{isImage(fileUrl) ? (
																	<a
																		href={fileUrl}
																		target='_blank'
																		rel='noopener noreferrer'
																		title='Preview image'
																	>
																		<img
																			src={fileUrl}
																			alt={fileTitle}
																			style={{
																				width: 40,
																				height: 40,
																				objectFit: "cover",
																				borderRadius: 4,
																				border: "1px solid #eee",
																				boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
																			}}
																		/>
																	</a>
																) : (
																	<span style={{ color: "#aaa", fontSize: 12 }}>
																		No preview
																	</span>
																)}
															</td>
															<td
																style={{ padding: 8, verticalAlign: "middle" }}
															>
																<Button
																	size='small'
																	className='mb-10'
																	type='primary'
																	onClick={() => downloadFile(fileUrl)}
																>
																	Download
																</Button>
																{comment.permissions?.can_edit && (
																	<Button
																		size='small'
																		className='mb-10'
																		type='primary'
																		danger
																		onClick={() =>
																			handleDeleteAttachment(
																				comment.id,
																				attachment.id,
																			)
																		}
																		style={{ marginLeft: 8 }}
																	>
																		Delete
																	</Button>
																)}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									) : null}
								</div>
							</li>
						)}
					/>
				)}

				{comment_status === 1 && (
					<div className='add-comment-box'>
						<Form form={form}>
							<Form.Item
								name='comment'
								// rules={[{ required: true, message: 'Please enter a comment or mention someone' }]}
							>
								{/* <Mentions
                ref={mentionInputRef}
                rows={3}
                maxLength={151}
                style={{ width: '100%' }}
                onSearch={handleSearchUsers}
                onChange={handleMentionChange}
                onSelect={handleMentionSelect}
                value={newComment}
                placeholder="Type '@' to mention someone"
              >
                {mentionSuggestions.map((suggestion) => (
                  <Mentions.Option key={suggestion.id} value={suggestion.value}>
                    {suggestion.label}
                  </Mentions.Option>
                ))}
              </Mentions> */}
								<div style={{ position: "relative" }}>
									<Mentions
										ref={mentionInputRef}
										autoSize={{ minRows: 3, maxRows: 15 }}
										maxLength={10000}
										style={{ width: "100%" }}
										onSearch={handleSearchUsers}
										onChange={handleMentionChange}
										onSelect={handleMentionSelect}
										value={newComment}
										options={mentionSuggestions} // Use the options prop
										placeholder="Type '@' to mention someone"
									/>
									<div
										style={{
											textAlign: "right",
											color: "gray",
											fontSize: "12px",
											marginTop: "4px",
										}}
									>
										{(newComment || "").length} / 10000
									</div>
								</div>
							</Form.Item>

							<div className='mention-tags'>
								{mentionedUsers.map((user) => (
									<Tag
										key={user.id}
										closable
										onClose={() => handleTagClose(user.id)}
										closeIcon={<CloseOutlined />}
									>
										{user.label}
									</Tag>
								))}
							</div>

							<Form.Item>
								<Upload.Dragger {...props}>
									<p className='ant-upload-drag-icon'>
										<InboxOutlined />
									</p>
									<p className='ant-upload-text'>
										Drag and drop files here or click to upload
									</p>
								</Upload.Dragger>
							</Form.Item>

							<Form.Item>
								<Button
									type='primary'
									onClick={handleAddComment}
									loading={loading}
								>
									Add Comment
								</Button>
								&nbsp;&nbsp;
								<Button
									type='primary'
									onClick={handleAddComment}
									loading={loading}
								>
									Add Attachment
								</Button>
							</Form.Item>
						</Form>
					</div>
				)}
			</Col>
		</Row>
	);
};

export default CommentSystem;
